import 'core/api/debug';

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import WordQueue from 'com/WordQueue';
import {
  apiGetPendingWords,
  apiGetClosestPendingTime,
  apiGetDeck,
  apiGetWord,
} from 'core/api';
import { getDecksListUrl } from 'core/paths';
import { ErrorPane } from 'core/ui/Error';
import { FramePreloader } from 'core/ui/Frame';

import EmptyMessage from './EmptyMessage';
import useWordsList from './useWordsList';

type TProps = {
  exercise: keyof Pick<TExerciseEnum, 'wordCards' | 'wordCardsReverse'>;
};

function WordCardsEngine({ exercise }: TProps) {
  const [error, setError] = useState<TAnyError | null>(null);
  const { wordUid } = useParams<{ wordUid?: string }>();
  const [words, setWords, onUpdateWord] = useWordsList(exercise, wordUid);
  const [deck, setDeck] = useState<TDeck | null>(null);
  const [closestTime, setClosestTime] = useState<string | null>(null);

  const deckUid = orDie(useParams<{ deckUid?: string }>().deckUid);

  const init = useCallback(async () => {
    setWords(null);

    const [words, deck] = await Promise.all([
      wordUid
        ? apiGetWord(wordUid).then(w => [w])
        : apiGetPendingWords(deckUid, exercise),
      apiGetDeck(deckUid),
    ] as const);
    setWords(words);
    setDeck(deck);

    if (words.length) return;

    const closestTime = await apiGetClosestPendingTime(deckUid, exercise);
    setClosestTime(closestTime);
  }, []);

  const historyPush = useNavigate();
  const onFinish = useCallback(() => {
    void historyPush(getDecksListUrl());
  }, [historyPush]);

  useEffect(() => {
    init().catch(setError);
  }, []);

  return error ? (
    <ErrorPane error={error} showStackTrace />
  ) : (
    <>
      {!words && <FramePreloader />}
      {deck && words && words.length > 0 && (
        <WordQueue
          key="queue"
          words={words}
          deck={deck}
          exercise={exercise}
          onFinish={onFinish}
          onUpdateWord={onUpdateWord}
        />
      )}
      {words?.length === 0 && (
        <EmptyMessage
          key="empty"
          exercise={exercise}
          closestTime={closestTime}
        />
      )}
    </>
  );
}

export const WordCards = () => (
  <WordCardsEngine exercise={TExercise.wordCards} />
);
export const Translate = () => (
  <WordCardsEngine exercise={TExercise.wordCardsReverse} />
);
